import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Mail, Phone, MapPin, Facebook, Twitter, Instagram, Linkedin } from 'lucide-react';
import LogoPng from '../assets/InitX_Logo.png';

const FooterContainer = styled.footer`
  background: rgba(15, 23, 42, 0.95) !important;
  backdrop-filter: blur(10px) !important;
  color: white !important;
  padding: 60px 20px 20px;
  margin-top: 80px;
  border-top: 1px solid rgba(0, 212, 255, 0.2) !important;
  box-shadow: 0 -2px 20px rgba(0,212,255,0.2) !important;
  position: relative;
  z-index: 999;
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 40px;
  margin-bottom: 40px;
  
  @media (max-width: 1024px) {
    grid-template-columns: 2fr 1fr 1fr;
    gap: 30px;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const FooterSection = styled.div`
  h3 {
    font-size: 1.2rem;
    margin-bottom: 20px;
    color: white;
    font-family: 'Inter', sans-serif;
    font-weight: 600;
  }
  
  p, li {
    color: #94a3b8;
    line-height: 1.6;
    margin-bottom: 10px;
    font-family: 'Inter', sans-serif;
  }
  
  ul {
    list-style: none;
    padding: 0;
  }
  
  a {
    color: white !important;
    text-decoration: none !important;
    font-weight: 500;
    padding: 8px 16px;
    border-radius: 8px;
    transition: all 0.3s ease;
    position: relative;
    display: inline-block;
    font-family: 'Inter', sans-serif;
    
    &:hover {
      background: rgba(0, 212, 255, 0.1) !important;
      color: #00d4ff !important;
    }
  }
`;

const FooterLogo = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  transition: transform 0.3s ease;
  cursor: pointer;
  
  img {
    height: 40px;
    width: auto;
    margin-right: 10px;
  }
  
  .logo-text {
    font-size: 2rem;
    font-weight: 700;
    color: white;
    font-family: 'Inter', sans-serif;
  }
  
  &:hover {
    transform: scale(1.02);
  }
`;

const ContactInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;
  color: #94a3b8;
  font-family: 'Inter', sans-serif;
  
  a {
    color: white !important;
    padding: 4px 8px;
    display: inline !important;
    cursor: pointer;
    
    &:hover {
      color: #00d4ff !important;
      background: transparent !important;
    }
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 20px;
`;

const SocialLink = styled.a`
  width: 40px;
  height: 40px;
  background: rgba(0, 212, 255, 0.1) !important;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white !important;
  transition: all 0.3s ease;
  text-decoration: none !important;
  
  &:hover {
    background: linear-gradient(135deg, #0ea5e9 0%, #00d4ff 50%, #3b82f6 100%) !important;
    color: white !important;
    transform: translateY(-2px);
    box-shadow: 0 0 20px rgba(0, 212, 255, 0.4);
  }
`;

const FooterBottom = styled.div`
  border-top: 1px solid rgba(0, 212, 255, 0.2);
  padding-top: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #94a3b8;
  font-family: 'Inter', sans-serif;
  
  p {
    margin: 0;
    font-family: 'Inter', sans-serif;
  }
  
  a {
    transition: all 0.3s ease;
    padding: 4px 8px;
    border-radius: 4px;
    color: white !important;
    text-decoration: none !important;
    font-family: 'Inter', sans-serif;
    
    &:hover {
      color: #00d4ff !important;
      background: rgba(0, 212, 255, 0.1) !important;
    }
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
    text-align: center;
  }
`;

const Footer = () => {
  return (
    <FooterContainer>
      <FooterContent>
        <FooterGrid>
          <FooterSection>
            <FooterLogo>
              <img src={LogoPng} alt="InitX" />
              <span className="logo-text">InitX</span>
            </FooterLogo>
            <p>
              InitX Technologies - Initiating Innovation with Expertise.
              We are a trusted IT consulting and software development company specializing in web development, AI solutions, cloud services, and comprehensive digital transformation. We partner with businesses of all sizes to deliver cutting-edge technology solutions with enterprise-grade security standards.
            </p>

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '15px', marginBottom: '15px' }}>
              <img
                src="/images/msme-official.png"
                alt="MSME Certificate"
                style={{
                  width: '120px',
                  height: '70px',
                  border: '1px solid #B8860B',
                  borderRadius: '4px'
                }}
              />
              <span style={{ color: '#87ceeb', fontWeight: '600', fontSize: '14px' }}>MSME Registered Enterprise</span>
            </div>


            <ContactInfo>
              <Mail size={16} />
              <a href="mailto:contact@initx.online">contact@initx.online</a>
            </ContactInfo>
            <ContactInfo>
              <Phone size={16} />
              <a href="https://wa.me/918986283243" target="_blank" rel="noopener noreferrer">
                +91-8986283243
              </a>
            </ContactInfo>

            {/* Two Locations Added */}
            {/* Registered Office - Hidden
            <ContactInfo>
              <MapPin size={16} />
              <div>
                <strong>Registered Office:</strong><br />
                Sasaram, Rohtas, Bihar - 821115
              </div>
            </ContactInfo>
            */}
            <ContactInfo>
              <MapPin size={16} />
              <div>
                <strong>Corporate Office:</strong><br />
                {/* Change this with your second location */}
                Lucknow, Uttar Pradesh - 226013
              </div>
            </ContactInfo>

            <SocialLinks>
              <SocialLink href="https://facebook.com/initxtech" aria-label="Facebook">
                <Facebook size={20} />
              </SocialLink>
              <SocialLink href="https://twitter.com/initxtech" aria-label="Twitter">
                <Twitter size={20} />
              </SocialLink>
              <SocialLink href="https://instagram.com/initxtechnologies" aria-label="Instagram">
                <Instagram size={20} />
              </SocialLink>
              <SocialLink href="https://www.linkedin.com/company/initxtechnologies/" aria-label="LinkedIn">
                <Linkedin size={20} />
              </SocialLink>
            </SocialLinks>
          </FooterSection>

          <FooterSection>
            <h3>Company</h3>
            <ul>
              <li><Link to="/about" onClick={() => window.scrollTo(0, 0)}>About Us</Link></li>
              <li><Link to="/services" onClick={() => window.scrollTo(0, 0)}>Services</Link></li>
              <li><Link to="/products" onClick={() => window.scrollTo(0, 0)}>Products</Link></li>
              <li><Link to="/blogs" onClick={() => window.scrollTo(0, 0)}>Blogs</Link></li>
              <li><Link to="/careers" onClick={() => window.scrollTo(0, 0)}>Careers</Link></li>
              <li><Link to="/contact" onClick={() => window.scrollTo(0, 0)}>Contact</Link></li>
              <li><Link to="/ourteam" onClick={() => window.scrollTo(0, 0)}>Our Team</Link></li>
            </ul>
          </FooterSection>

          <FooterSection>
            <h3>Support</h3>
            <ul>
              <li><Link to="/contact" onClick={() => window.scrollTo(0, 0)}>Help Center</Link></li>
              <li><Link to="/contact" onClick={() => window.scrollTo(0, 0)}>Contact Support</Link></li>
            </ul>
          </FooterSection>
        </FooterGrid>

        <FooterBottom>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <p>&copy; 2026 InitX Technologies. All rights reserved.</p>
            <div style={{ 
              padding: '8px 12px',
              borderRadius: '4px',
              border: '1px solid rgba(135, 206, 235, 0.3)',
              background: 'rgba(135, 206, 235, 0.1)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span style={{ fontSize: '12px' }}>🏆</span>
              <span style={{ color: '#87ceeb', fontSize: '12px', fontWeight: '500' }}>MSME Registered</span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
            <Link to="/privacy" onClick={() => window.scrollTo(0, 0)}>Privacy Policy</Link>
            <Link to="/terms" onClick={() => window.scrollTo(0, 0)}>Terms of Service</Link>
            <Link to="/cookies" onClick={() => window.scrollTo(0, 0)}>Cookie Policy</Link>
          </div>
        </FooterBottom>
      </FooterContent>
    </FooterContainer>
  );
};

export default Footer;